import { useParams } from 'react-router-dom';
import { Upload, FileText, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import DORADiffView from '@/components/dora/DORADiffView';
import api from '@/services/api';
import { useDocumentWorkflow, BaseDocument } from '@/hooks/useDocumentWorkflow';

interface RFPDocument extends BaseDocument {}

export function RFPProjectView() {
  const { projectId } = useParams<{ projectId: string }>();
  
  const initializeRFPProject = async () => {
    try {
      await api.get(`/rfp/projects/${projectId}/rfp`);
    } catch (error: any) {
      console.error('Failed to initialize RFP project:', error);
    }
  };

  const {
    currentStep,
    setCurrentStep,
    loading,
    analyzing,
    analysisPhase,
    elapsedTime,
    selectedDocuments,
    diffViewData,
    templateDocument,
    referenceDocuments,
    setSelectedDocuments,
    handleUploadReference,
    handleUploadTemplate,
    handleAnalyzeAndFill,
    handleDeleteDocument,
    handleBackToUpload,
  } = useDocumentWorkflow<RFPDocument>({
    module: 'rfp',
    projectId: projectId!,
    onInitialize: initializeRFPProject,
  });

  const handleDeleteSelected = async () => {
    if (selectedDocuments.length === 0) return;
    try {
      await Promise.all(selectedDocuments.map(docId => handleDeleteDocument(docId)));
      setSelectedDocuments([]);
    } catch (error: any) {
      console.error('Failed to delete documents:', error);
    }
  };

  const toggleDocumentSelection = (documentId: number) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDocuments.length === referenceDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(referenceDocuments.map(doc => doc.document_id));
    }
  };

  const canAnalyze = referenceDocuments.length > 0 && templateDocument !== undefined;

  if (diffViewData) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <Button
          variant="ghost"
          onClick={handleBackToUpload}
          className="mb-4"
        >
          ← Back to Upload
        </Button>
        <DORADiffView
          projectId={parseInt(projectId!)}
          templateDocId={diffViewData.templateDocId}
          generatedDocId={diffViewData.generatedDocId}
          changes={diffViewData.changes}
          summary={diffViewData.summary}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">RFP Response Workflow</h1>
        <p className="text-muted-foreground">
          Upload reference RFP responses, provide a blank template, and let AI fill it automatically
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8 relative">
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-border -z-10" />
        
        <StepIndicator
          number={1}
          title="Upload Documents"
          active={currentStep === 'upload'}
          completed={currentStep !== 'upload'}
        />
        <StepIndicator
          number={2}
          title="Analyze & Fill"
          active={currentStep === 'analyze'}
          completed={currentStep === 'complete'}
        />
        <StepIndicator
          number={3}
          title="Review & Edit"
          active={currentStep === 'complete'}
          completed={false}
        />
      </div>

      {/* Upload Section */}
      {currentStep === 'upload' && (
        <div className="space-y-6">
          {/* Reference Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Reference Documents
              </CardTitle>
              <CardDescription>
                Upload previously completed RFP responses. The system will learn your answers from these.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".docx,.pdf"
                  onChange={(e) => e.target.files && handleUploadReference(e.target.files)}
                  className="hidden"
                  id="reference-upload"
                  disabled={loading}
                />
                <label htmlFor="reference-upload" className="cursor-pointer">
                  <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                  <div className="text-sm font-medium mb-1">
                    Drop files here or click to browse
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Accepts: .docx, .pdf
                  </div>
                </label>
              </div>

              {referenceDocuments.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.length === referenceDocuments.length && referenceDocuments.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="text-sm font-medium">Uploaded References ({referenceDocuments.length})</span>
                    </div>
                    {selectedDocuments.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteSelected}
                        disabled={loading}
                      >
                        Delete {selectedDocuments.length} selected
                      </Button>
                    )}
                  </div>
                  {referenceDocuments.map((doc, idx) => (
                    <div
                      key={doc.document_id}
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.includes(doc.document_id)}
                          onChange={() => toggleDocumentSelection(doc.document_id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Badge variant="secondary">Reference {idx + 1}</Badge>
                        <span className="text-sm font-medium">{doc.filename}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDocument(doc.document_id)}
                        disabled={loading}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Template Document */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-500" />
                Template Document
              </CardTitle>
              <CardDescription>
                Upload the blank RFP template that needs to be filled out. Only one template allowed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!templateDocument ? (
                <div className="border-2 border-dashed border-orange-200 rounded-lg p-8 text-center hover:border-orange-400 transition-colors">
                  <input
                    type="file"
                    accept=".docx"
                    onChange={(e) => e.target.files?.[0] && handleUploadTemplate(e.target.files[0])}
                    className="hidden"
                    id="template-upload"
                    disabled={loading}
                  />
                  <label htmlFor="template-upload" className="cursor-pointer">
                    <Upload className="h-10 w-10 mx-auto mb-4 text-orange-500" />
                    <div className="text-sm font-medium mb-1">
                      Drop template here or click to browse
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Accepts: .docx only
                    </div>
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-orange-500">Template</Badge>
                    <span className="text-sm font-medium">{templateDocument.filename}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteDocument(templateDocument.document_id)}
                    disabled={loading}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Button */}
          <div className="flex flex-col items-center gap-4">
            <Button
              size="lg"
              onClick={handleAnalyzeAndFill}
              disabled={!canAnalyze || analyzing}
              className="min-w-[300px]"
            >
              {analyzing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                  Analyzing...
                </>
              ) : (
                <>
                  Analyze & Fill Template
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            
            {!canAnalyze && (
              <Alert className="max-w-md">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {!templateDocument && 'Upload a template document'}
                  {templateDocument && referenceDocuments.length === 0 && 'Upload at least 1 reference document'}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      )}

      {/* Analyzing Step */}
      {currentStep === 'analyze' && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950">
          <CardContent className="p-8">
            <div className="flex flex-col items-center gap-6">
              <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {analysisPhase === 'template' && '🤖 Analyzing Template...'}
                {analysisPhase === 'extraction' && '🔍 Extracting Answers...'}
                {analysisPhase === 'complete' && '✅ Analysis Complete!'}
              </h3>
              
              <div className="text-center space-y-3 max-w-2xl">
                {analysisPhase === 'template' && (
                  <p className="text-xl font-medium text-blue-800 dark:text-blue-200">
                    Analyzing RFP template<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'extraction' && (
                  <p className="text-xl font-medium text-green-800 dark:text-green-200">
                    Extracting answers from {referenceDocuments.length} document(s)<span className="animate-pulse">...</span>
                  </p>
                )}
                
                {analysisPhase === 'complete' && (
                  <>
                    <p className="text-lg font-bold text-green-700 dark:text-green-300">
                      ✨ Analysis Complete!
                    </p>
                    <div className="text-sm text-blue-700 dark:text-blue-300 mt-3 space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>Duration: {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <Progress 
                value={analysisPhase === 'template' ? 33 : analysisPhase === 'extraction' ? 66 : 100} 
                className="w-full max-w-md h-2" 
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complete Step */}
      {currentStep === 'complete' && (
        <Card>
          <CardHeader>
            <CardTitle>RFP Response Complete</CardTitle>
            <CardDescription>
              Your RFP response has been generated. Review and download below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-medium mb-4">
                RFP response generation complete!
              </p>
              <Button onClick={() => setCurrentStep('upload')}>
                Start New RFP
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface StepIndicatorProps {
  number: number;
  title: string;
  active: boolean;
  completed: boolean;
}

function StepIndicator({ number, title, active, completed }: StepIndicatorProps) {
  return (
    <div className="flex flex-col items-center gap-2 bg-background px-4 relative z-10">
      <div
        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
          completed
            ? 'bg-primary border-primary text-primary-foreground'
            : active
            ? 'bg-primary/10 border-primary text-primary'
            : 'bg-background border-border text-muted-foreground'
        }`}
      >
        {completed ? <CheckCircle2 className="h-5 w-5" /> : number}
      </div>
      <div
        className={`text-sm font-medium whitespace-nowrap ${
          active || completed ? 'text-foreground' : 'text-muted-foreground'
        }`}
      >
        {title}
      </div>
    </div>
  );
}
